import { Box } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import { type ComponentProps, useState } from 'react';
import { Document, Page } from 'react-pdf';

import DialogTitleWithCloseButton from '~/components/DialogTitleWithCloseButton';
import { ZoomControls } from '~/components/documentReview/ZoomControls';

interface AttachmentPreviewDialogProps {
  open: boolean;
  // May be undefined when the attachment has no URL on the wire; react-pdf's
  // `Document file` renders nothing in that case (an inert preview).
  filePath: string | undefined;
  onClose: () => void;
  pageNumber?: number;
}

function AttachmentPreviewDialog({
  open,
  filePath,
  onClose,
  pageNumber,
}: AttachmentPreviewDialogProps) {
  const [baseWidth, setBaseWidth] = useState<number | undefined>(undefined);
  const [scale, setScale] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [previewKey, setPreviewKey] = useState<string>(`${filePath}:${pageNumber ?? 'all'}`);

  const currentKey = `${filePath}:${pageNumber ?? 'all'}`;
  if (previewKey !== currentKey) {
    setPreviewKey(currentKey);
    setNumPages(0);
    setBaseWidth(undefined);
  }

  const scaledWidth = baseWidth ? baseWidth * scale : undefined;
  const renderAllPages = pageNumber === undefined;

  // Size the dialog to the first page. We read the width off react-pdf's own
  // <Page onLoadSuccess> instead of calling pdf.getPage() ourselves: react-pdf
  // owns the page's load lifecycle and cancels it on unmount, so a preview that
  // is torn down mid-load (dialog closed or file changed) can't leave a rejected
  // getPage() promise dangling as an unhandled rejection (Sentry
  // ASSET-MANAGEMENT-WQ).
  const handleFirstPageLoad: NonNullable<ComponentProps<typeof Page>['onLoadSuccess']> = (page) => {
    const pageWidth = page.view[2] - page.view[0];
    if (pageWidth) {
      setBaseWidth(pageWidth);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      disableScrollLock
      sx={{
        '& > .MuiDialog-container > .MuiPaper-root': {
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: baseWidth ? `${baseWidth + 20}px` : '100%',
        },
      }}
    >
      <DialogTitleWithCloseButton onClose={onClose} title="Preview Document" />

      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
          }}
        >
          <Document file={filePath} onLoadSuccess={(pdf) => setNumPages(pdf.numPages)}>
            {renderAllPages ? (
              Array.from({ length: numPages }, (_, index) => (
                <Page
                  key={`page-${index + 1}`}
                  pageNumber={index + 1}
                  width={scaledWidth}
                  onLoadSuccess={index === 0 ? handleFirstPageLoad : undefined}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                />
              ))
            ) : (
              <Page
                pageNumber={pageNumber}
                width={scaledWidth}
                onLoadSuccess={handleFirstPageLoad}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />
            )}
          </Document>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            pb: 2,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <ZoomControls scale={scale} onScaleChange={setScale} />
        </Box>
      </Box>
    </Dialog>
  );
}

export default AttachmentPreviewDialog;
